import config from '@/config.ts'
import api from './index.ts'
import type {
  WorkspaceResponse,
  TaskResponse,
  TaskOutputResponse,
  WorkspaceBase,
  Task,
  Asset,
  AssetResponse,
  DownloadAssetResponse,
  FolderContentsResponse,
  LocationResponse,
} from '@/types/workspaces.ts'
import type { SharedUser } from '@/types/auth.ts'

export async function getWorkspaces(params = {}) {
  const res = await api.get(`${config.backendUrl}/api/workspaces/`, params)
  return res.data as WorkspaceResponse
}

export async function createWorkspace(data: WorkspaceBase) {
  return await api.post(`${config.backendUrl}/api/workspaces/`, data)
}

export async function updateWorkspace(workspaceId: string, data: WorkspaceBase) {
  return await api.put(`${config.backendUrl}/api/workspaces/${workspaceId}`, data)
}

export async function deleteWorkspace(workspaceId: string) {
  return await api.delete(`${config.backendUrl}/api/workspaces/${workspaceId}`)
}
export async function deleteTask(workspaceId: string, taskId: string) {
  return await api.delete(`${config.backendUrl}/api/workspaces/${workspaceId}/tasks/${taskId}`)
}

export async function duplicateWorkspace(workspaceId: string) {
  return await api.post(`${config.backendUrl}/api/workspaces/${workspaceId}/duplicate/`)
}

export async function getTasks(
  workspaceId: string,
  params: Record<string, any> = { ordering: '-created_at' },
) {
  const res = await api.get(`${config.backendUrl}/api/workspaces/${workspaceId}/tasks/`, params)
  return res.data as TaskResponse
}

export async function createTask(workspaceId: string, data: any) {
  const res = await api.post(`${config.backendUrl}/api/workspaces/${workspaceId}/tasks/`, data)
  return res.data as Task
}

export async function uploadFileToTask(workspaceId: string, taskId: string, file: File) {
  const formData = new FormData()
  formData.append('images', file)
  return await api.post(
    `${config.backendUrl}/api/workspaces/${workspaceId}/tasks/${taskId}/upload/`,
    formData,
  )
}

export async function getTaskOutput(
  workspaceId: string,
  taskId: string,
  params = { f: 'json', line: 0, limit: '-502' },
) {
  const res = await api.get(
    `${config.backendUrl}/api/workspaces/${workspaceId}/tasks/${taskId}/output/`,
    params,
  )
  return res.data as TaskOutputResponse
}

export async function exportAsset(workspaceId: string, taskId: string, asset: string, data: any) {
  return await api.post(
    `${config.backendUrl}/api/workspaces/${workspaceId}/tasks/${taskId}/${asset}/export`,
    data,
  )
}

export async function postTaskAction(
  workspaceId: string,
  taskId: string,
  action: string,
  data: any,
) {
  return await api.post(
    `${config.backendUrl}/api/workspaces/${workspaceId}/tasks/${taskId}/${action}`,
    data,
  )
}

export async function editTask(workspaceId: string, taskId: string, data: any) {
  const res = await api.put(
    `${config.backendUrl}/api/workspaces/${workspaceId}/tasks/${taskId}`,
    data,
  )
  return res.data as Task
}

export async function getTask(workspaceId: string, taskId: string) {
  const res = await api.get(
    `${config.backendUrl}/api/workspaces/${workspaceId}/tasks/${taskId}`
  )
  return res.data as Task
}

export async function searchAssets(workspaceId: string, params = {}) {
  const res = await api.post(
    `${config.backendUrl}/api/workspaces/${workspaceId}/assets/search`,
    params,
  )
  return res.data as AssetResponse
}

export async function getWorkspaceAssets(workspaceId: string, params = {}) {
  const res = await api.get(`${config.backendUrl}/api/workspaces/${workspaceId}/assets/`, params)
  return res.data as AssetResponse
}

export async function getAsset(workspaceId: string, assetId: string) {
  const res = await api.get(`${config.backendUrl}/api/workspaces/${workspaceId}/assets/${assetId}`)
  return res.data as Asset
}

export async function createAsset(workspaceId: string, data: any) {
  const res = await api.post(`${config.backendUrl}/api/workspaces/${workspaceId}/assets/`, data)
  return res.data as Asset
}

export async function resolveAssets(workspaceId: string, assetIds: string[]) {
  const res = await api.post(`${config.backendUrl}/api/workspaces/${workspaceId}/assets/resolve`, {
    asset_ids: assetIds,
  })
  return res.data as { found: Asset[]; missing: string[] }
}

export async function getFolderContents(workspaceId: string, params = {}) {
  const res = await api.get(
    `${config.backendUrl}/api/workspaces/${workspaceId}/folders/contents/`,
    params,
  )
  return res.data as FolderContentsResponse
}

export async function deleteWorkspaceAssets(workspaceId: string, assetId: string) {
  return await api.delete(`${config.backendUrl}/api/workspaces/${workspaceId}/assets/${assetId}`)
}

export async function getLinkDownload(workspaceId: string, assetId: string) {
  const res = await api.get(
    `${config.backendUrl}/api/workspaces/${workspaceId}/assets/${assetId}/download`,
  )
  return res.data as DownloadAssetResponse
}

export type TableExportFormat = 'csv' | 'xlsx' | 'sqlite' | 'parquet'
export type TableExportResponse =
  | { status: 'ready'; download_url: string }
  | { status: 'pending'; task_id: string }

export async function exportTable(workspaceId: string, assetId: string, format: TableExportFormat) {
  const res = await api.post(
    `${config.backendUrl}/api/workspaces/${workspaceId}/assets/${assetId}/export`,
    { format },
  )
  return res.data as TableExportResponse
}

export async function createFolder(workspaceId: string, data: any) {
  return await api.post(`${config.backendUrl}/api/workspaces/${workspaceId}/folders/`, data)
}

export async function updateAsset(workspaceId: string, assetId: string, data: any) {
  return await api.put(`${config.backendUrl}/api/workspaces/${workspaceId}/assets/${assetId}`, data)
}

export type ProjectSaveResult =
  { conflict: false; asset: Asset } | { conflict: true; current: Asset }

/** PUT with an optimistic-concurrency check; a 409 asset_conflict resolves instead of throwing. */
export async function updateProjectAsset(
  workspaceId: string,
  assetId: string,
  data: any,
  expectedUpdatedAt: string | null,
): Promise<ProjectSaveResult> {
  const res = await api.put(
    `${config.backendUrl}/api/workspaces/${workspaceId}/assets/${assetId}`,
    data,
    {
      params: expectedUpdatedAt ? { expected_updated_at: expectedUpdatedAt } : undefined,
      validateStatus: (s: number) => (s >= 200 && s < 300) || s === 409,
    },
  )
  if (res.status !== 409) return { conflict: false, asset: res.data as Asset }
  const detail = res.data?.detail
  if (detail?.code !== 'asset_conflict') throw detail?.message ?? 'Failed to save project'
  return { conflict: true, current: detail.current as Asset }
}

export async function updateFolder(workspaceId: string, folderId: string, data: any) {
  return await api.put(`${config.backendUrl}/api/workspaces/${workspaceId}/folders/${folderId}`, data)
}

export interface FolderDeleteResult {
  deleted_folder_ids: string[]
  deleted_asset_ids: string[]
  deleted_project_ids: string[]
}

/** Deletes the folder with all its sub-folders and assets. */
export async function deleteFolder(workspaceId: string, folderId: string) {
  const res = await api.delete(
    `${config.backendUrl}/api/workspaces/${workspaceId}/folders/${folderId}`,
  )
  return res.data as FolderDeleteResult
}

export async function getLocations() {
  const res = await api.get(`${config.backendUrl}/api/locations/`)
  return res.data as LocationResponse
}

export async function getSharedWorkspaces() {
  const res = await api.get(`${config.backendUrl}/api/workspaces/shared`)
  return res.data as WorkspaceResponse
}

export async function shareWorkspace(workspaceId: string, users: string[]) {
  return await api.put(`${config.backendUrl}/api/workspaces/${workspaceId}/shared_users`, {
    user_ids: users,
  })
}

export async function getSharedUsers(workspaceId: string) {
  const res = await api.get(`${config.backendUrl}/api/workspaces/${workspaceId}/shared_users`)
  return res.data as SharedUser[]
}

export async function getFolders(workspaceId: string, params = {}) {
  const res = await api.get(`${config.backendUrl}/api/workspaces/${workspaceId}/folders/`, params)
  return res.data as AssetResponse
}
